import { useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Plus, Server } from 'lucide-react';
import type { StorageAgentDTO } from '@prism/contracts';
import { api, errorDetail } from '../../api/client';
import { useAgents, useStorage, useWatchFolders } from '../../api/hooks';
import { queryKeys } from '../../api/queryKeys';
import { EmptyState } from '../../components/EmptyState';
import { Modal } from '../../components/Modal';
import { relative } from '../../lib/format';
import { EnrollmentInstructions } from './agents/AgentEnrollment';
import { AgentPane, PlainHttpBadge, StatusPill } from './agents/AgentPane';

export function AgentsAdmin() {
  return (
    <Routes>
      <Route index element={<AgentList />} />
      <Route path=":id" element={<AgentPane />} />
    </Routes>
  );
}

/** One line per agent; clicking opens its configuration. */
function AgentList() {
  const q = useAgents();
  const storage = useStorage().data?.items ?? [];
  const watch = useWatchFolders().data?.items ?? [];
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', site: '' });
  const [issued, setIssued] = useState<{ token: string; agentId: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const agents = q.data?.items ?? [];

  async function create() {
    setError(null);
    try {
      const res = await api<{ enrollmentToken: string; agent: StorageAgentDTO }>('/api/v1/agents', { method: 'POST', json: { name: form.name, site: form.site } });
      setIssued({ token: res.enrollmentToken, agentId: res.agent.id });
      await qc.invalidateQueries({ queryKey: queryKeys.agents() });
    } catch (err) {
      setError(errorDetail(err));
    }
  }

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ flex: 1 }}>
          <h2>Storage agents</h2>
          <p className="lede">A storage agent is a small service beside on-site storage (NAS, SAN, local disks) that gives PRISM access to it over an outbound connection. Click an agent to configure it: its storage, export and watch folders, name and site, and its enrolment.</p>
        </div>
        <button className="btn primary" onClick={() => { setIssued(null); setForm({ name: '', site: '' }); setAdding(true); }}><Plus size={15} /> Add agent</button>
      </div>
      {agents.length === 0 && !q.isLoading && (
        <EmptyState style={{ border: '1px dashed var(--border-strong)', borderRadius: 14 }} icon={<Server size={24} />} title="No storage agents yet">
          <p>Add an agent for each site whose storage PRISM should use, such as a studio NAS or an archive server.</p>
        </EmptyState>
      )}
      <div className="agent-list" data-testid="agents">
        {agents.map((a) => {
          const folders = storage.filter((l) => l.agent?.id === a.id);
          const watched = watch.filter((w) => w.agent?.id === a.id);
          const problems = folders.filter((l) => l.availability !== 'online').length;
          return (
            <button key={a.id} className="agent-row" data-agent={a.name} onClick={() => navigate(`/admin/agents/${a.id}`)} data-testid="agent-row">
              <Server size={18} color="var(--accent-strong)" />
              <span className="agent-row-main">
                <strong>{a.name}</strong>
                <span className="faint">{a.site}{a.hostname ? ` · ${a.hostname}` : ''} · last seen {relative(a.lastSeenAt)}</span>
              </span>
              <span className="muted" style={{ fontSize: 13 }}>
                {folders.filter((l) => l.purpose === 'storage').length} storage · {folders.filter((l) => l.purpose === 'export').length} export · {watched.length} watch{folders.some((l) => l.purpose === 'edit') ? ` · ${folders.filter((l) => l.purpose === 'edit').length} edit` : ''}
                {problems ? <span style={{ color: 'var(--warning)' }}> · {problems} offline</span> : null}
              </span>
              <PlainHttpBadge a={a} />
              <StatusPill a={a} />
              <ChevronRight size={16} className="faint" />
            </button>
          );
        })}
      </div>

      {adding && (
        <Modal title={issued ? 'Enrol the agent' : 'Add a storage agent'} wide={Boolean(issued)} onClose={() => setAdding(false)}
          actions={issued ? <button className="btn primary" onClick={() => { setAdding(false); navigate(`/admin/agents/${issued.agentId}`); }}>Open its configuration</button> : (
            <><button className="btn ghost" onClick={() => setAdding(false)}>Cancel</button><button className="btn primary" disabled={!form.name || !form.site} onClick={() => void create()}>Create enrolment token</button></>
          )}>
          {issued ? <EnrollmentInstructions token={issued.token} roots={[]} /> : (
            <>
              {error && <div className="banner error">{error}</div>}
              <div className="field"><label htmlFor="agent-name">Name</label><input id="agent-name" className="input" value={form.name} placeholder="Studio NAS" onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="field"><label htmlFor="agent-site">Site</label><input id="agent-site" className="input" value={form.site} placeholder="helsinki-studio" onChange={(e) => setForm({ ...form, site: e.target.value.toLowerCase() })} /><span className="help">Work on these files is routed to this site.</span></div>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}
